'use strict';

var constants = require('./constants');
var _ = require('lodash');

var InterfacePrototype = function InterfacePrototype() {
  var _types = {};
  var _postprocessors = {};
  var _preprocessors = {};

  /** -- OBJECT -- */

  this.fork = function fork_Interface() {
    return new InterfaceType();
  };

  /** -- CONFIGURATION -- */

  this.configure = function configure(c) {
    if (c) {
      this._internalConfiguration = _.merge({}, constants.DEFAULT_CONFIGURATION, this._internalConfiguration, c);
      this._configurationQueue = [this._internalConfiguration];
      return this;
    }

    return this._internalConfiguration;
  };

  this.configurePush = function configurePush(c) {
    this._internalConfiguration = _.merge({}, constants.DEFAULT_CONFIGURATION, c);
    this._configurationQueue.push(this._internalConfiguration);
    return this._internalConfiguration;
  };

  /**
   * call after Interface#configurePush to restore the previous configuration
   */
  this.configurePop = function configurePop() {
    var c = this._configurationQueue.pop();

    if (_.size(this._configurationQueue) > 0) {
      this._internalConfiguration = _.last(this._configurationQueue);
    }

    return this._internalConfiguration;
  };

  /** @deprecated */
  this.configuration = this.configure;

  /** -- DATA TYPE -- */

  /** @type {type} */
  this.discoverType = constants.discoverType;

  /** add a new type for Interface to use */
  this.type = function type_Interface(name, definition) {
    if (definition === undefined) {
      return _types[name];
    }

    _types[name] = definition;
    return this;
  };

  /** @returns array of type names supported */
  this.typesSupported = function typesSupported_Interface() {
    return _.keys(_types);
  };

  /** -- PREPROCESSORS -- */

  /** add a new preprocessors for Interface to use */
  this.preprocessor = function preprocessor_Interface(name, definition) {
    if (definition === undefined) {
      return _preprocessors[name];
    }

    _preprocessors[name] = definition;
    return this;
  };

  /** @returns array of preprocessors names supported */
  this.preprocessorsSupported = function preprocessorsSupported_Interface() {
    return _.keys(_preprocessors);
  };

  /** add a new preprocessors for Interface to use */
  this.postprocessor = function postprocessor_Interface(name, definition) {
    if (definition === undefined) {
      return _postprocessors[name];
    }

    _postprocessors[name] = definition;
    return this;
  };

  /** -- POSTPROCESSORS -- */

  /** @returns array of preprocessors names supported */
  this.postprocessorsSupported = function postprocessorsSupported_Interface() {
    return _.keys(_postprocessors);
  };

  /** -- PUBLIC METHODS -- */

  this.implementObject = function implementObject(dtd, list) {
    return _.zipObject(_.keys(list), this.implementArray(dtd, list));
  };

  this.implementArray = function implementArray(dtd, list) {
    return _.map(
      list,
      function (o) {
        return this.make(dtd, o);
      },
      this
    );
  };

  this.generate = function generate(dtd) {
    var generatedObject;

    this.configurePush({
      integritySafeguards: false
    });
    generatedObject = this.implement(dtd, undefined, {});
    this.configurePop();

    return generatedObject;
  };

  this.validate = function validate(dtd, source) {
    try {
      this.configurePush({
        integritySafeguards: true,
        defaultToRequired: true,
        failFast: true
      });
      this.make(dtd, source);
      this.configurePop();

      return true;
    } catch (e) {
      return false;
    }
  };

  this.gateway = function gateway(dtd, source, sync) {
    return _.merge(sync, source, this.make(dtd, source));
  };

  this.merge = function gateway(dtd, syncSource) {
    return _.merge(syncSource, this.make(dtd, syncSource));
  };

  this.make = function make(dtd, source) {
    return this.implement(dtd, source, {});
  };

  this.implement = function implement(dtd, source, sync) {
    var implemented;

    this._dtdQueue.push(sync);
    implemented = this._implement(dtd, source, sync);
    this._dtdQueue.pop();

    return implemented;
  };

  this.mock = function mock(dtd) {
    var mocked;

    this._dtdQueue.push(sync);
    mocked = this._mock(dtd);
    this._dtdQueue.pop();

    return mocked;
  };

  /** -- PRIVATE METHODS -- */

  this._mock = function _mock(dtd) {
    this.activeMethod = '_mock';
    return parse.call(this, {
      dtd: dtd,
      source: {},
      sync: {},
      syncKey: syncKeyConvertToInternalKey,
      process: function mockProcess(ruleset) {
        if (!ruleset.typeRequirement.mockValue) {
          return;
        }

        ruleset.sync[ruleset.syncKey] = ruleset.typeRequirement.mockValue.call(
          this,
          ruleset.typeRequirement,
          ruleset.dtdRequirement,
          this._dtdQueue
        );
      }
    });
  };

  /**
   * produce a version of the object that is ready for serialization to json
   *
   * @param dtd the data type definition to follow
   * @param source Actually the data source to serialize to the initial version
   * @return synced object data mirroring source, but in the original format
   * @deprecated
   */
  this.serialize = function serialize(dtd, actuallySync) {
    var syncFromSource = {};

    console.debug('Interface#serialize is @deprecated');

    this.activeMethod = 'serialize';
    return parse.call(this, {
      dtd: dtd,
      source: _.cloneDeep(actuallySync),
      sync: syncFromSource,
      syncKey: syncKeyConvertToInternalKey,
      process: function implementProcess(ruleset) {
        ruleset.source[ruleset.syncKey] = postprocessType.call(
          this,
          ruleset.dtdRequirement,
          ruleset.source[ruleset.syncKey],
          'convertToSource'
        );

        ruleset.sync[ruleset.sourceKey] = !!ruleset.typeRequirement.convertToSource
          ? ruleset.typeRequirement.convertToSource.call(
              this,
              ruleset.source[ruleset.syncKey],
              ruleset.dtdRequirement,
              ruleset.source,
              ruleset.sync
            )
          : ruleset.source[ruleset.syncKey];

        ruleset.sync[ruleset.sourceKey] = preprocessType.call(
          this,
          ruleset.dtdRequirement,
          ruleset.sync[ruleset.sourceKey],
          'convertToSource'
        );
      }
    });
  };

  /**
   * iterates over the dtd and pulls good values out of the data object into
   * the obj object.
   */
  this._implement = function _implement(dtd, sourceObject, syncObject) {
    this.activeMethod = '_implement';
    return parse.call(this, {
      dtd: dtd,
      sync: syncObject,
      source: sourceObject,
      syncKey: syncKeyConvertToInternalKey,
      process: function implementProcess(ruleset) {
        var isRequired, doesSourceMeetCriteria, syncValue;

        var sourceProp = preprocessType.call(
          this,
          ruleset.dtdRequirement,
          ruleset.source[ruleset.sourceKey],
          'convertToSync'
        );

        if (ruleset.typeRequirement.convertToSync) {
          sourceProp = ruleset.typeRequirement.convertToSync.call(
            this,
            sourceProp,
            ruleset.dtdRequirement,
            ruleset.source,
            ruleset.sync
          );
        }

        doesSourceMeetCriteria = ruleset.typeRequirement.isOfType.call(this, sourceProp, ruleset.dtdRequirement);

        isRequired =
          ruleset.dtdRequirement.required === undefined && ruleset.dtdRequirement.optional === undefined
            ? this._internalConfiguration.defaultToRequired
            : !!ruleset.dtdRequirement.required || !ruleset.dtdRequirement.optional;

        if (!doesSourceMeetCriteria && !!this._internalConfiguration.integritySafeguards && !!isRequired) {
          if (!!this._internalConfiguration.failFast) {
            throw new EABError('utility-js-interface', 201, {
              requirements: ruleset.dtdRequirement,
              property: ruleset.sourceKey,
              source: ruleset.source
            });
          } else {
            this._internalConfiguration.logFailureFn(
              'interface: required property ' + ruleset.sourceKey + ' or ' + ruleset.sourceKey,
              ruleset.dtdRequirement
            );
          }
        }

        syncValue = !!doesSourceMeetCriteria
          ? sourceProp
          : getDefaultValue(
              ruleset.dtdRequirement.defaultValue,
              ruleset.typeRequirement.defaultValue,
              ruleset.dtdRequirement,
              sourceProp
            );

        if (ruleset.typeRequirement.afterConvertToSync) {
          syncValue = ruleset.typeRequirement.afterConvertToSync(syncValue, ruleset.dtdRequirement);
        }

        if (ruleset.dtdRequirement.afterConvertToSync) {
          syncValue = ruleset.dtdRequirement.afterConvertToSync(syncValue, ruleset.dtdRequirement);
        }

        ruleset.sync[ruleset.syncKey] = postprocessType.call(this, ruleset.dtdRequirement, syncValue, 'convertToSync');
      }
    });
  };

  /**  */
  var parse = function parse(ruleset) {
    var dtdRequirement, sourceKey, sourceProp, typeRequirement, syncKey;

    if (!ruleset.dtd) {
      return ruleset.source;
    }

    if (!ruleset.source) {
      ruleset.source = {};
    }

    if (_.isString(ruleset.dtd)) {
      ruleset.dtd = {
        __singleDataValue__: ruleset.dtd
      };
    }

    if (isSingularValueOnDTD(ruleset.dtd)) {
      ruleset.source = {
        __singleDataValue__: ruleset.source
      };

      ruleset.sync = {
        __singleDataValue__: ruleset.sync
      };
    }

    for (var key in ruleset.dtd) {
      dtdRequirement = ruleset.dtd[key] = getDTDRequirements.call(this, ruleset.dtd, key);
      sourceKey = getSourceKey.call(this, dtdRequirement, ruleset.source, key);
      syncKey = ruleset.syncKey.call(this, dtdRequirement, sourceKey);

      // recursive descent
      if (constants.MATCH_DESCENDANT_KEY.test(sourceKey)) {
        syncKey = syncKey.replace(constants.MATCH_DESCENDANT_KEY, '');
        ruleset.sync[syncKey] = ruleset.sync[syncKey] || {};
        sourceProp = ruleset.source[syncKey];

        this[this.activeMethod](dtdRequirement, sourceProp, ruleset.sync[syncKey], ruleset);
      }

      // single value definition
      else {
        typeRequirement = resolveTypeRequirements.call(this, dtdRequirement.type);

        if (!typeRequirement) {
          throw new EABError('utility-js-interface', 100, {
            type: dtdRequirement.type.toString()
          });
        }

        ruleset.process.call(this, {
          typeRequirement: typeRequirement,
          dtdRequirement: dtdRequirement,
          source: ruleset.source,
          sourceKey: sourceKey,
          sync: ruleset.sync,
          syncKey: syncKey
        });
      }
    }

    if (isSingularValueOnDTD(ruleset.dtd)) {
      ruleset.sync = ruleset.sync.__singleDataValue__;
    }

    return ruleset.sync;
  };

  /**
   *
   * @param dtdRequirement
   * @param sourceProp
   * @param fnName
   * @returns {*}
   */
  var preprocessType = function preprocessType_Interface(dtdRequirement, sourceProp, fnName) {
    var preprocessor;

    if (!dtdRequirement.preprocessor) {
      return sourceProp;
    }

    if (_.isPlainObject(dtdRequirement.preprocessor) && _.isFunction(dtdRequirement.preprocessor[fnName])) {
      preprocessor = dtdRequirement.preprocessor;
    } else if (!!_preprocessors[dtdRequirement.preprocessor]) {
      preprocessor = _preprocessors[dtdRequirement.preprocessor];
    }

    if (!preprocessor || !_.isFunction(preprocessor[fnName])) {
      throw new EABError('utility-js-interface', 101, {
        preprocessor: dtdRequirement.preprocessor
      });
    }

    return preprocessor[fnName](sourceProp, dtdRequirement, this._dtdQueue);
  };

  /**
   *
   * @param dtdRequirement
   * @param syncProp
   * @param fnName
   * @returns {*}
   */
  var postprocessType = function postprocessType_Interface(dtdRequirement, syncProp, fnName) {
    var postprocessor;

    if (!dtdRequirement.postprocessor) {
      return syncProp;
    }

    if (!!dtdRequirement.postprocessor && _.isFunction(dtdRequirement.postprocessor[fnName])) {
      postprocessor = dtdRequirement.postprocessor;
    } else if (!!_postprocessors[dtdRequirement.postprocessor]) {
      postprocessor = _postprocessors[dtdRequirement.postprocessor];
    }

    if (!postprocessor || !_.isFunction(postprocessor[fnName])) {
      throw new EABError('utility-js-interface', 102, {
        postprocessor: dtdRequirement.postprocessor
      });
    }

    return postprocessor[fnName](syncProp, dtdRequirement, this._dtdQueue);
  };

  /**
   *
   * @param dtd
   * @returns {boolean}
   */
  var isSingularValueOnDTD = function isSingularValueOnDTD(dtd) {
    return _.size(dtd) === 1 && dtd.__singleDataValue__ !== undefined;
  };

  /**
   *
   * @param dtdRequirement
   * @param sourceKey
   * @returns {string}
   */
  var syncKeyConvertToInternalKey = function syncKeyConvertToInternalKey(dtdRequirement, sourceKey) {
    return !!dtdRequirement.writeToKey ? dtdRequirement.writeToKey : sourceKey;
  };

  /**
   *
   * @param dtd
   * @param key
   * @returns {{type: *}}
   */
  var getDTDRequirements = function getDTDRequirements(dtd, key) {
    var req = dtd[key];

    return _.isString(req) ? { type: req } : req;
  };

  /**
   * @note potential issue if synonym cannot be used to lookup values in a
   *   collection
   * @param dtdReq to look through for properties
   * @param source object to find value of property for
   * @param key key to use if propertyName is not specified
   * @returns {*}
   */
  var getSourceKey = function getSourceKey(dtdReq, sourceProp, key) {
    var propertyType;
    var property = dtdReq.synonyms || dtdReq.synonym || dtdReq.propertyName;

    if (property === undefined) {
      return key;
    }

    propertyType = constants.discoverType(property);

    if (propertyType === 'function') {
      return property(sourceProp);
    } else if (propertyType === 'array') {
      return _.findKey(
        sourceProp,
        function (v, k) {
          return _.contains(this, k);
        },
        property
      );
    } else {
      return property;
    }
  };

  /**
   *
   * @param typeName
   * @returns {*}
   */
  var resolveTypeRequirements = function resolveTypeRequirements(typeName) {
    return constants.discoverType(typeName) === 'function' ? typeName : _types[typeName];
  };

  /**
   *
   * @param requirementDefaultValue
   * @param typeDefaultValue
   * @param dtdRequirement
   * @param sourceProp
   * @returns {*}
   */
  var getDefaultValue = function getDefaultValue(
    requirementDefaultValue,
    typeDefaultValue,
    dtdRequirement,
    sourceProp
  ) {
    var resolvedRequirementDefaultValue = resolveDefaultValue(requirementDefaultValue, sourceProp, dtdRequirement);
    return resolvedRequirementDefaultValue !== undefined
      ? resolvedRequirementDefaultValue
      : resolveDefaultValue(typeDefaultValue, sourceProp, dtdRequirement);
  };

  /**
   *
   * @param defaultValue
   * @param source
   * @param requirement
   * @returns {*}
   */
  var resolveDefaultValue = function resolveDefaultValue(defaultValue, source, requirement) {
    return _.isFunction(defaultValue) ? defaultValue(source, requirement) : defaultValue;
  };
};

/**
 * @global
 * @class Interface
 */
var InterfaceType = function InterfaceType() {
  this._internalConfiguration = _.clone(constants.DEFAULT_CONFIGURATION);
  this._configurationQueue = [this._internalConfiguration];
  this._dtdQueue = [];
};

InterfaceType.prototype = new InterfacePrototype();
var interfaceType = new InterfaceType();

interfaceType.type('default', {
  defaultValue: undefined,
  isOfType: function verifyTypeDefault(val, req) {
    return constants.discoverType(val) === req.type;
  }
});

module.exports = interfaceType;



// WEBPACK FOOTER //
// ./app/vendor/utility-js-interface/src/core.js